import {
  ApiResponse,
  PaginationMeta,
  type PaginationParams,
  fetchPaginated,
} from "@/services/api-service";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useState } from "react";

// Simple hook for pagination state and data fetching
export function usePagination<
  T,
  P extends PaginationParams = PaginationParams,
>({
  endpoint,
  queryKey,
  initialParams,
  options = {
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  },
}: {
  endpoint: string;
  queryKey: string | string[];
  initialParams?: P;
  options?: {
    enabled?: boolean;
    staleTime?: number;
    gcTime?: number;
    refetchOnWindowFocus?: boolean;
  };
}) {
  // Pagination state
  const [params, setParams] = useState<P>(initialParams as P);

  // Query key can be string or array
  const fullQueryKey = Array.isArray(queryKey)
    ? [...queryKey, params]
    : [queryKey, params];

  // Fetch data with React Query
  const query = useQuery<ApiResponse<T>, Error>({
    queryKey: fullQueryKey,
    queryFn: () => fetchPaginated<T, P>(endpoint, params),
    ...options,
  });

  // Update setParams to handle extended params
  const updateParams = useCallback((newParams: Partial<P>) => {
    setParams((prev) => ({ ...prev, ...newParams }));
  }, []);

  // Pagination actions
  const setPage = useCallback((page: number) => {
    setParams((prev) => ({ ...prev, currentPage: page }));
  }, []);

  const setLimit = useCallback((limit: number) => {
    setParams((prev) => ({ ...prev, pageSize: limit, currentPage: 1 }));
  }, []);

  const setSearch = useCallback((search: string) => {
    setParams((prev) => ({ ...prev, q: search, currentPage: 1 }));
  }, []);

  const setSort = useCallback((sort: string) => {
    setParams((prev) => ({ ...prev, s: sort, currentPage: 1 }));
  }, []);

  // Return everything needed for pagination
  return {
    // Query results
    data: (query.data?.payload as T[]) || [],
    meta: (query.data?.pagination as PaginationMeta) || {},
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    isRefetching: query.isRefetching,

    // Pagination state
    params,

    // Pagination actions
    setPage,
    setLimit,
    setSearch,
    setSort,
    setParams: updateParams,

    // For advanced usage
    query,
  };
}
